import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Badge } from 'reactstrap';

// Right slider with the full breakdown of one Done-For-You order. Domains are the main rows;
// each expands into its mailboxes (dropdown), so long orders stay compact.
const ZapmailOrderSidebar = ({ isOpen, onClose, order }) => {
    const [expanded, setExpanded] = useState(null); // null = auto (open when the order is small)
    useEffect(() => { setExpanded(null); }, [order?.['_id']]);
    if (!order) return null;

    const retail = order['compedByAdmin'] ? 0 : (order['totals']?.['retail'] || 0);
    const cost = order['totals']?.['cost'] || 0;
    const domainItems = (order['items'] || []).filter(item => item['kind'] !== 'mailbox_plan');
    const mailboxItems = (order['items'] || []).filter(item => item['kind'] === 'mailbox_plan');

    // Mailboxes grouped under their domain — including domains not purchased in this order
    const mailboxesByDomain = {};
    for (const item of mailboxItems) for (const box of item['mailboxes'] || []) (mailboxesByDomain[box['domainName']] = mailboxesByDomain[box['domainName']] || []).push(box);
    const extraDomains = Object.keys(mailboxesByDomain).filter(name => !domainItems.some(item => item['domainName'] === name));
    const totalMailboxes = Object.values(mailboxesByDomain).reduce((count, boxes) => count + boxes.length, 0);
    const mailboxStatus = mailboxItems[0]?.['status'];
    const monthlyRetail = mailboxItems.reduce((sum, item) => sum + (item['retailAmount'] || 0), 0);
    const monthlyCost = mailboxItems.reduce((sum, item) => sum + (item['costAmount'] || 0), 0);

    const autoOpen = totalMailboxes <= 8;
    const isDomainOpen = (name) => expanded ? expanded.has(name) : autoOpen;
    const toggleDomain = (name) => setExpanded(prev => {
        const next = new Set(prev || (autoOpen ? [...domainItems.map(item => item['domainName']), ...extraDomains] : []));
        if (next.has(name)) next.delete(name); else next.add(name);
        return next;
    });

    // One accordion row per domain: purchase info on the row, its mailboxes as the dropdown
    const domainRow = ({ name, status, paid, itemCost, forwardingDomain, isExisting }) => {
        const boxes = mailboxesByDomain[name] || [];
        const open = isDomainOpen(name);
        return (
            <div className='zapmail-order-sb-domain' key={name}>
                <div className='zapmail-order-sb-domain-row' onClick={() => boxes.length && toggleDomain(name)}>
                    <i className={`fa fa-angle-right zapmail-order-sb-chevron ${open && boxes.length ? 'open' : ''} ${boxes.length ? '' : 'hidden'}`}></i>
                    <div className='zapmail-order-sb-domain-info'>
                        <span className='zapmail-order-sb-domain-name'>{name}</span>
                        {forwardingDomain && <span className='zapmail-order-sb-sub'>forwards to {forwardingDomain}</span>}
                        {isExisting && <span className='zapmail-order-sb-sub'>existing domain — mailboxes only</span>}
                    </div>
                    {Boolean(boxes.length) && <span className='zapmail-order-sb-count'>{boxes.length} mailbox{boxes.length === 1 ? '' : 'es'}</span>}
                    {status && <Badge color={ITEM_COLORS[status] || 'secondary'}>{status}</Badge>}
                    <span className='zapmail-order-sb-money'>{isExisting ? '' : order['compedByAdmin'] ? '—' : formatCents(paid)}</span>
                    <span className='zapmail-order-sb-money muted'>{isExisting ? '' : formatCents(itemCost)}</span>
                </div>
                {open && Boolean(boxes.length) && (
                    <div className='zapmail-order-sb-mail-grid'>
                        {boxes.map(box => (
                            <div className='zapmail-order-sb-mail-chip' key={`${box['mailboxUsername']}@${name}`} title={`${box['firstName']} ${box['lastName']}`}>
                                <span className='zapmail-order-sb-mail-email'>{box['mailboxUsername']}@{name}</span>
                                {box['zapmailMailboxId']
                                    ? <i className='fa fa-check-circle text-success' title='Connected'></i>
                                    : <i className='fa fa-clock-o text-warning' title='Pending'></i>}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <Modal isOpen={isOpen} toggle={onClose} modalClassName='right right-without-bg' backdrop={false} className='main-modal right right-side-modal style-three'>
            <ModalHeader toggle={onClose}>
                <h3>Order Details</h3>
            </ModalHeader>
            <ModalBody>
                <div className='zapmail-order-sb'>
                    <div className='zapmail-order-sb-top'>
                        <div>
                            <div className='zapmail-order-sb-workspace'>{order['workspaceId']?.['name'] || '—'}</div>
                            {order['workspaceId']?.['zapmail']?.['workspaceId'] && <div className='zapmail-order-sb-zid'>Zapmail: sellagent-{order['workspaceId']['zapmail']['workspaceId']}</div>}
                            <div className='zapmail-order-sb-meta'>Placed by {userName(order['userId'])} · {formatDate(order['createdAt'])}{order['paidAt'] ? ` · Paid ${formatDate(order['paidAt'])}` : ''}</div>
                        </div>
                        <div className='zapmail-order-sb-badges'>
                            <Badge color={STATUS_COLORS[order['status']] || 'secondary'}>{STATUS_LABELS[order['status']] || order['status']}</Badge>
                            {order['compedByAdmin'] && <Badge color='info' className='ml-1'>Admin comped</Badge>}
                        </div>
                    </div>

                    <div className='zapmail-order-sb-fin'>
                        <div className='zapmail-order-sb-fin-tile'>
                            <span>Customer paid</span>
                            <strong>{order['compedByAdmin'] ? '—' : formatCents(order['totals']?.['retail'])}</strong>
                            {order['compedByAdmin'] && <em>comped</em>}
                        </div>
                        <div className='zapmail-order-sb-fin-tile'>
                            <span>Our Zapmail cost</span>
                            <strong>{formatCents(cost)}</strong>
                            <em>{order['stripeSubscriptionId'] ? 'Stripe subscription' : order['compedByAdmin'] ? 'no billing (admin)' : 'one-time'}</em>
                        </div>
                        <div className='zapmail-order-sb-fin-tile'>
                            <span>Profit</span>
                            <strong className={retail - cost >= 0 ? 'text-success' : 'text-danger'}>{formatCents(retail - cost)}</strong>
                        </div>
                    </div>

                    <div className='zapmail-order-sb-section'>
                        <h6>
                            Domains &amp; Mailboxes ({domainItems.length + extraDomains.length} domain{domainItems.length + extraDomains.length === 1 ? '' : 's'} · {totalMailboxes} mailbox{totalMailboxes === 1 ? '' : 'es'})
                            {mailboxStatus && <Badge color={ITEM_COLORS[mailboxStatus] || 'secondary'} className='ml-1'>{mailboxStatus}</Badge>}
                            {Boolean(totalMailboxes) && <span className='zapmail-order-sb-monthly'>{order['compedByAdmin'] ? `${formatCents(monthlyCost)}/mo cost` : `${formatCents(monthlyRetail)}/mo · ${formatCents(monthlyCost)} cost`}</span>}
                        </h6>
                        <div className='zapmail-order-sb-domain-list'>
                            <div className='zapmail-order-sb-domain-head'>
                                <span className='zapmail-order-sb-domain-head-label'>Domain</span>
                                <span className='zapmail-order-sb-money'>Paid</span>
                                <span className='zapmail-order-sb-money muted'>Cost</span>
                            </div>
                            {domainItems.map(item => domainRow({ name: item['domainName'], status: item['status'], paid: item['retailAmount'], itemCost: item['costAmount'], forwardingDomain: item['forwardingDomain'], isExisting: false }))}
                            {extraDomains.map(name => domainRow({ name, isExisting: true }))}
                        </div>
                    </div>

                    {order['lastError'] && (
                        <div className='zapmail-order-sb-section'>
                            <h6>Last Error</h6>
                            <div className='zapmail-order-sb-error'>{order['lastError']}</div>
                        </div>
                    )}
                </div>
            </ModalBody>
        </Modal>
    );
};

const STATUS_LABELS = { paid: 'Paid', provisioning: 'Provisioning', completed: 'Completed', partially_failed: 'Partially Failed', failed: 'Failed', refunded: 'Refunded', pending_payment: 'Pending Payment', canceled: 'Canceled' };
const STATUS_COLORS = { paid: 'primary', provisioning: 'info', completed: 'success', partially_failed: 'warning', failed: 'danger', refunded: 'secondary', pending_payment: 'warning', canceled: 'secondary' };
const ITEM_COLORS = { pending: 'warning', purchased: 'info', provisioning: 'info', completed: 'success', failed: 'danger', refunded: 'secondary' };

const formatCents = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const formatDate = (value) => value ? new Date(value).toLocaleDateString() : '—';
const userName = (user) => user && typeof user === 'object' ? (`${user['firstName'] || ''} ${user['lastName'] || ''}`.trim() || user['email'] || '—') : '—';

export default ZapmailOrderSidebar;
